// Inside a realm, laid out across its home land: its name; its ages as bands; its events along a line; its
// people along their lifetimes. Zooming in is how you read deeper. With the whole realm in view, events are
// one line each; as an age fills the screen it becomes where you are, and its summary appears; closer still,
// events open into cards with their whole story. Every word follows the reading rule in world.ts, and nothing
// ever overlaps: each block of text takes its own place in a grid, the most important first.

import { agesAt, fmtYear, lifeRows, pagesOf } from "@hoee/chart";
import type { Age, Chart, Happening, Life, Pages, Realm } from "@hoee/chart";
import type { Chronicle } from "@hoee/schema";
import type { Labels, TextStyle } from "./gl/labels.ts";
import type { Rules } from "./gl/rules.ts";
import { FONTS } from "./theme.ts";
import type { Camera } from "./view/camera.ts";
import { Fades } from "./fade.ts";
import { smooth, spanText } from "./world.ts";

const TITLE: TextStyle = { font: FONTS.display };
const SUB: TextStyle = { font: FONTS.text, italic: true };
const AGE: TextStyle = { font: FONTS.displaySc };
const EVENT: TextStyle = { font: FONTS.text, weight: 600 };
const EVENT_MINOR: TextStyle = { font: FONTS.text };
const DATE: TextStyle = { font: FONTS.text, italic: true };
const BODY: TextStyle = { font: FONTS.text };
const PERSON: TextStyle = { font: FONTS.text };
const ROLE: TextStyle = { font: FONTS.text, italic: true };

/** The grid text is packed into, in px. */
const CELL = 11;

const index: Promise<string[]> = fetch("data/chronicles/index.json")
  .then((r) => (r.ok ? (r.json() as Promise<string[]>) : []))
  .catch(() => []);
const cache = new Map<string, Promise<{ chronicle: Chronicle; pages: Pages } | null>>();

/** A realm's chronicle as written and as laid out, fetched once and kept. Realms without one resolve to null. */
export function loadChronicle(id: string): Promise<{ chronicle: Chronicle; pages: Pages } | null> {
  let p = cache.get(id);
  if (!p) {
    p = index.then((ids) =>
      ids.includes(id)
        ? fetch(`data/chronicles/${id}.json`)
            .then((r) => (r.ok ? (r.json() as Promise<Chronicle>) : null))
            .then((chronicle) => (chronicle ? { chronicle, pages: pagesOf(chronicle) } : null))
            .catch(() => null)
        : null,
    );
    cache.set(id, p);
  }
  return p;
}

/** A realm's chronicle, laid out for drawing. */
export function loadPages(id: string): Promise<Pages | null> {
  return loadChronicle(id).then((c) => c?.pages ?? null);
}

/** Horizontal intervals already taken in one row of the grid. */
class Row {
  private readonly taken: [number, number][] = [];
  fits(a: number, b: number): boolean {
    return !this.taken.some(([p, q]) => a < q && p < b);
  }
  take(a: number, b: number): void {
    this.taken.push([a, b]);
  }
}

/** A grid of thin rows; a block of text takes as many rows as it is tall. */
class Grid {
  readonly rows: Row[];
  constructor(count: number) {
    this.rows = Array.from({ length: Math.max(0, count) }, () => new Row());
  }
  /** The first row where a block fits, its leader line reaching it through free rows above. */
  place(x0: number, x1: number, height: number, leaderX: number | null): number {
    const k = Math.ceil(height / CELL);
    for (let r = 0; r + k <= this.rows.length; r++) {
      let ok = true;
      for (let i = r; i < r + k && ok; i++) ok = (this.rows[i] as Row).fits(x0, x1);
      for (let i = 0; i < r && ok && leaderX != null; i++) ok = (this.rows[i] as Row).fits(leaderX - 3, leaderX + 3);
      if (ok) {
        for (let i = r; i < r + k; i++) (this.rows[i] as Row).take(x0, x1);
        if (leaderX != null) for (let i = 0; i < r; i++) (this.rows[i] as Row).take(leaderX - 3, leaderX + 3);
        return r;
      }
    }
    return -1;
  }
}

/** Something on the page you can open or enter, where it was drawn this frame. */
export interface Hit {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  happening?: Happening;
  life?: Life;
  age?: Age;
  /** The realm's name and the line under it: the way to its curiosities and further reading. */
  title?: true;
}

type Mode = "line" | "story";

export class ChronicleView {
  realm: Realm | null = null;
  pages: Pages | null = null;
  /** 0..1: how far the page has opened. */
  opacity = 0;
  /** What can be opened or entered, as drawn this frame. */
  hits: Hit[] = [];
  /** The open card's event or person, kept lit. */
  selected: string | null = null;
  /** The event under the pointer: its dot and the line to it light up. */
  pointed: string | null = null;
  /** The ages you are in now, outermost first: the one filling the screen, and its parents. */
  trail: Age[] = [];
  private readonly fades = new Fades();
  private byImportance: Happening[] = [];
  private readonly chart: Chart;
  private readonly labels: Labels;
  private readonly rules: Rules;
  private readonly now = new Date().getFullYear();
  private readonly wrapped = new Map<string, string[]>();

  constructor(chart: Chart, labels: Labels, rules: Rules) {
    this.chart = chart;
    this.labels = labels;
    this.rules = rules;
  }

  show(realm: Realm | null, pages: Pages | null): void {
    this.realm = realm;
    this.pages = pages;
    this.byImportance = pages
      ? [...pages.happenings].sort((a, b) => a.event.importance - b.event.importance || a.at - b.at)
      : [];
    this.trail = [];
  }

  /** Where an event or a person is on screen now (for keeping its card beside it). */
  anchorOf(id: string): Hit | null {
    return this.hits.find((h) => (h.happening?.event.id ?? h.life?.person.id) === id) ?? null;
  }

  /** Breaks text into lines no wider than `width` px at `size`; past `max` lines the last ends in an ellipsis. */
  private wrap(text: string, style: TextStyle, size: number, width: number, max: number): string[] {
    const key = `${style.font}|${style.weight ?? 400}|${style.italic ? 1 : 0}|${Math.round((width / size) * 4)}|${max}|${text}`;
    const hit = this.wrapped.get(key);
    if (hit) return hit;
    const L = this.labels;
    const em = width / size;
    const lines: string[] = [];
    let line = "";
    for (const word of text.split(" ")) {
      const next = line ? `${line} ${word}` : word;
      if (line && L.measure(next, style) > em) {
        lines.push(line);
        line = word;
      } else line = next;
    }
    if (line) lines.push(line);
    let out = lines;
    if (lines.length > max) {
      out = lines.slice(0, max);
      let last = `${out[max - 1]} ${lines.slice(max).join(" ")}`;
      while (last.length > 1 && L.measure(`${last}…`, style) > em) last = last.slice(0, -1);
      out[max - 1] = `${last.trimEnd()}…`;
    }
    this.wrapped.set(key, out);
    return out;
  }

  /** Draws lines of text left-aligned from x, the first baseline-centred at y. */
  private text(
    lines: string[],
    style: TextStyle,
    size: number,
    x: number,
    y: number,
    lead: number,
    alpha: number,
    tone = 0,
  ) {
    lines.forEach((line, i) => {
      const w = this.labels.measure(line, style) * size;
      this.labels.draw({ text: line, style, x: x + w / 2, y: y + i * lead, size, alpha, tone });
    });
  }

  /** A sheet of paper laid on the paint, for text that should read like a page. */
  private sheet(x0: number, y0: number, x1: number, y1: number, alpha: number, lit = false) {
    this.rules.draw({ x0: x0 - 2, y0: y0 + 2, x1: x1 + 2, y1: y1 + 6, alpha: 0.16 * alpha, pigment: -3, radius: 12 });
    this.rules.draw({ x0, y0, x1, y1, alpha: (lit ? 1 : 0.93) * alpha, pigment: -2, radius: 10 });
  }

  /** Lays out this frame. Returns whether anything is still fading. */
  layout(camera: Camera, dt: number): boolean {
    this.hits = [];
    const realm = this.realm;
    if (!realm || this.opacity < 0.01) return false;
    const k = 1 - Math.exp(-dt / 0.06);
    this.fades.begin();
    const L = this.labels,
      R = this.rules;
    const o = this.opacity;
    const { x0: ix0, x1: ix1 } = camera.inner;
    // Its story runs over the years it holds its home, in the part of that land it holds throughout: so every
    // word sits on its own paint.
    const end = Math.min(realm.home.to, this.chart.axis.end);
    const lo = Math.max(ix0, camera.xOf(realm.home.from)),
      hi = Math.min(ix1, camera.xOf(end));
    const { lane, part } = realm.home;
    const py0 = camera.yOf(part === "right" ? lane + 0.5 : lane),
      py1 = camera.yOf(part === "left" ? lane + 0.5 : lane + 1);
    const H = py1 - py0;
    if (hi - lo < 80 || H < 140) return false;
    const pages = this.pages;
    const left = lo + 20;

    // ---- Where you are: follows the middle of the screen as you scroll ----
    // Scrolling across a border moves you into the next age at once, with its summary: no click needed.
    // With (nearly) the whole realm in view, you are at the realm itself, not in one of its ages.
    const viewFrom = camera.yearAt(ix0),
      viewTo = camera.yearAt(ix1);
    const whole = viewTo - viewFrom >= 0.85 * (realm.home.to - realm.home.from);
    this.trail = pages && !whole ? agesAt(pages.ages, camera.yearAt(lo), camera.yearAt(hi)) : [];
    const here = this.trail.at(-1) ?? null;

    // ---- Its name, and where you are in it ----
    const titleSize = 26;
    const title = realm.name;
    const tw = L.measure(title, TITLE) * titleSize;
    if (tw < hi - lo - 40) {
      L.draw({ text: title, style: TITLE, x: left + tw / 2, y: py0 + 26, size: titleSize, alpha: o });
      if (this.pages) this.hits.push({ x0: left - 6, y0: py0 + 8, x1: left + tw + 6, y1: py0 + 40, title: true });
    }
    const sub = here
      ? `${here.period.name} · ${fmtYear(here.period.from)} – ${fmtYear(here.period.to)}`
      : `${spanText(realm, this.now)} · ${realm.tagline}`;
    const subSize = 14.5;
    const sw = L.measure(sub, here ? AGE : SUB) * subSize;
    if (sw < hi - lo - 40) {
      const a = o * this.fades.get(`sub:${here?.period.id ?? "realm"}`, true, k);
      L.draw({
        text: sub,
        style: here ? AGE : SUB,
        x: left + sw / 2,
        y: py0 + 50,
        size: subSize,
        alpha: a,
        tone: here ? 0 : 1,
      });
    }
    if (!pages) return this.fades.busy;
    // Beside the name: the summary of where you are (the age filling the screen, or the realm itself), set as
    // a paragraph you can read without opening anything.
    let headH = 66;
    const summary = here ? here.period.summary : "";
    const textX = left + Math.max(tw, sw) + 44;
    const textW = Math.min(820, hi - 20 - textX);
    if (summary && textW >= 280) {
      const size = 14.5,
        lead = 19;
      const lines = this.wrap(summary, BODY, size, textW, 5);
      const a = o * this.fades.get(`summary:${here?.period.id}`, true, k);
      this.rules.draw({
        x0: textX - 22,
        y0: py0 + 14,
        x1: textX - 21,
        y1: py0 + 14 + lines.length * lead,
        alpha: 0.3 * a,
      });
      this.text(lines, BODY, size, textX, py0 + 22, lead, a);
      headH = Math.max(headH, 28 + lines.length * lead);
    }

    // ---- Ages: a row of bands for each depth ----
    const ageRow = 24;
    const agesTop = py0 + headH;
    for (const age of pages.ages) {
      const { period } = age;
      const x0 = Math.max(camera.xOf(period.from), lo),
        x1 = Math.min(camera.xOf(period.to), hi);
      if (x1 - x0 < 2) continue;
      const y = agesTop + age.depth * ageRow;
      const pigment = (realm.color + 1 + age.order * 3 + age.depth * 5) % 10;
      const inHere = this.trail.includes(age);
      R.draw({
        x0: x0 + 1,
        y0: y + 2,
        x1: x1 - 1,
        y1: y + ageRow - 2,
        alpha: (inHere ? 1 : 0.85) * o,
        pigment,
        radius: 5,
      });
      const size = 13.5;
      const nw = L.measure(period.name, AGE) * size;
      const a = this.fades.get(`age:${period.id}`, x1 - x0 > nw + 20, k);
      if (a > 0)
        L.draw({
          text: period.name,
          style: AGE,
          x: Math.min(x0 + 10, x1 - nw - 10) + nw / 2,
          y: y + ageRow / 2,
          size,
          alpha: a * o,
        });
      this.hits.push({ x0, y0: y, x1, y1: y + ageRow, age });
    }

    // ---- Lives, along the bottom: always enough rows for every one of the most important ----
    const lifeRowH = 26;
    const seats = this.seats(pages.lives);
    const lifeRows = Math.min(8, Math.max(2, Math.floor((H * 0.24) / lifeRowH), seats.greats));
    const livesTop = py1 - 10 - lifeRows * lifeRowH;
    this.drawLives(camera, pages.lives, livesTop, lifeRows, lifeRowH, k, o, realm, lo, hi);

    // ---- Events ----
    const line = agesTop + pages.depth * ageRow + 14;
    const gridTop = line + 10;
    const grid = new Grid(Math.floor((livesTop - 8 - gridTop) / CELL));
    // The axis is a ruler: a tick where each event falls. An event with room for its title carries its own dot
    // at the start of the title, exactly above its date on the ruler; the rest stay ticks until you zoom in.
    R.draw({ x0: lo, y0: line - 0.5, x1: hi, y1: line + 0.5, alpha: 0.35 * o });
    let visible = 0;
    const ticks: { x: number; id: string }[] = [];
    for (const h of pages.happenings) {
      const x = camera.xOf(h.at);
      if (x < lo || x > hi) continue;
      visible++;
      ticks.push({ x, id: h.event.id });
    }
    const titled = new Map<string, number>();
    // How much room each event has decides how much of it can be read: its title, or its whole story.
    const spacing = (hi - lo) / Math.max(1, visible);
    const prefer: Mode = spacing >= 55 ? "story" : "line";

    for (const h of this.byImportance) {
      const ev = h.event;
      const id = `ev:${ev.id}`;
      const x = camera.xOf(h.at);
      if (x < lo || x > hi) {
        this.fades.get(id, false, 1);
        continue;
      }
      const date = ev.date ? `${ev.date} ${fmtYear(ev.year)}` : `${ev.approx ? "c. " : ""}${fmtYear(ev.year)}`;
      const titleSize = ev.importance === 1 ? 15.5 : 14.5;
      const style = ev.importance === 3 ? EVENT_MINOR : EVENT;
      const r = ev.importance === 1 ? 5 : ev.importance === 2 ? 4 : 3;
      const dw = L.measure(date, DATE) * 12.5;
      const head = dw + 8 + L.measure(ev.title, style) * titleSize;
      // The richest form the room allows, then a plainer one; the most important are placed first.
      const modes: Mode[] = prefer === "story" ? ["story", "line"] : ["line"];
      let placed: {
        x0: number;
        textX: number;
        w: number;
        y: number;
        height: number;
        story: string[];
        back: boolean;
      } | null = null;
      for (const mode of modes) {
        let textW = head,
          height = 22,
          story: string[] = [];
        if (mode === "story") {
          textW = Math.min(420, Math.max(head, 300));
          story = this.wrap(ev.text, BODY, 14, textW, 5);
          height = 24 + story.length * 18 + 2;
        }
        const w = 2 * r + 7 + textW;
        // The dot sits exactly at its date: the text runs on from it, or near the right edge ends at it.
        const forward = x - r >= lo + 2 && x - r + w <= hi - 4;
        const backward = x + r - w >= lo + 2;
        if (!forward && !backward) continue;
        const x0 = forward ? x - r : x + r - w;
        const row = grid.place(x0 - 10, x0 + w + 12, height + 6, null);
        if (row >= 0) {
          const textX = forward ? x + r + 7 : x0;
          placed = { x0, textX, w, y: gridTop + row * CELL, height, story, back: !forward };
          break;
        }
      }
      const a = this.fades.get(id, placed != null, k);
      if (!placed || a <= 0) continue;
      titled.set(ev.id, a);
      const { x0, textX, w, y, height, story } = placed;
      const lit = this.selected === ev.id;
      const pointed = lit || this.pointed === ev.id;
      if (lit) this.sheet(x0 - 8, y - 4, x0 + w + 8, y + height + 2, a * o, true);
      // Pointing at an event lights its tick on the ruler and draws a faint guide down to it.
      if (pointed) R.draw({ x0: x - 0.5, y0: line + 3, x1: x + 0.5, y1: y + 10 - r - 2, alpha: 0.35 * a * o });
      const dr = pointed ? r + 1.5 : r;
      R.draw({ x0: x - dr, y0: y + 10 - dr, x1: x + dr, y1: y + 10 + dr, alpha: a * o, radius: dr });
      L.draw({ text: date, style: DATE, x: textX + dw / 2, y: y + 10, size: 12.5, alpha: a * o, tone: 1 });
      L.draw({
        text: ev.title,
        style,
        x: textX + dw + 8 + (L.measure(ev.title, style) * titleSize) / 2,
        y: y + 10,
        size: titleSize,
        alpha: a * o,
      });
      if (story.length) this.text(story, BODY, 14, textX, y + 32, 18, a * o);
      this.hits.push({ x0: x0 - 6, y0: y - 2, x1: x0 + w + 6, y1: y + height, happening: h });
    }
    // Ticks last, now that we know which events carry titles: theirs are firm, the rest faint.
    for (const t of ticks) {
      const a = titled.get(t.id) ?? 0;
      const lit = this.pointed === t.id || this.selected === t.id;
      const half = lit ? 7 : 3 + 2 * a;
      R.draw({ x0: t.x - 0.6, y0: line - half, x1: t.x + 0.6, y1: line + half, alpha: (lit ? 1 : 0.3 + 0.5 * a) * o });
    }
    return this.fades.busy;
  }

  /** Rows for this chronicle's lives, most important first; worked out once per chronicle. */
  private seated: { lives: Life[]; rows: Map<Life, number>; greats: number } | null = null;
  private seats(lives: Life[]): { rows: Map<Life, number>; greats: number } {
    if (this.seated?.lives !== lives) {
      const rows = lifeRows(lives);
      const greats = 1 + Math.max(-1, ...lives.filter((l) => l.person.importance === 1).map((l) => rows.get(l) ?? -1));
      this.seated = { lives, rows, greats };
    }
    return this.seated;
  }

  private drawLives(
    camera: Camera,
    lives: Life[],
    top: number,
    rows: number,
    rowH: number,
    k: number,
    o: number,
    realm: Realm,
    ix0: number,
    ix1: number,
  ): void {
    const L = this.labels,
      R = this.rules;
    const seated = this.seats(lives).rows;
    const size = 13.5;
    const pigment = (realm.color + 5) % 10;
    // In each row, the next life's start limits how far a name may run.
    const byRow = new Map<number, Life[]>();
    for (const life of lives) {
      const row = seated.get(life);
      if (row == null || row >= rows) continue;
      byRow.set(row, [...(byRow.get(row) ?? []), life]);
    }
    for (const [row, list] of byRow) {
      list.sort((a, b) => a.reach[0] - b.reach[0]);
      const y = top + row * rowH;
      const barY0 = y + rowH - 8,
        barY1 = y + rowH - 3;
      list.forEach((life, i) => {
        // Lives reaching beyond the realm's years are drawn only within them.
        const r0 = camera.xOf(life.reach[0]),
          r1 = camera.xOf(life.reach[1]);
        if (r1 < ix0 || r0 > ix1) return;
        const imp = life.person.importance;
        const base = (imp === 1 ? 0.85 : 0.6) * o;
        if (life.known === "both") {
          const x0 = Math.max(r0, ix0),
            x1 = Math.min(r1, ix1);
          R.draw({ x0, y0: barY0, x1: Math.max(x1, x0 + 6), y1: barY1, alpha: base, pigment, radius: 2.5 });
        } else {
          // What the record does not give fades away: after a birth, before a death, either side of a floruit.
          // A dot marks the one year that is known.
          const steps = 10;
          for (let j = 0; j < steps; j++) {
            const t0 = j / steps,
              t1 = (j + 1) / steps,
              t = (t0 + t1) / 2;
            const fade = life.known === "born" ? 1 - t : life.known === "died" ? t : 1 - Math.abs(t - 0.5) * 2;
            const xa = Math.max(r0 + (r1 - r0) * t0, ix0),
              xb = Math.min(r0 + (r1 - r0) * t1, ix1);
            if (xb > xa)
              R.draw({ x0: xa, y0: barY0 + 1, x1: xb + 0.5, y1: barY1 - 1, alpha: base * 0.75 * fade * fade, pigment });
          }
          const kx = camera.xOf(life.known === "died" ? life.to : life.from);
          if (kx >= ix0 && kx <= ix1)
            R.draw({
              x0: kx - 4,
              y0: (barY0 + barY1) / 2 - 4,
              x1: kx + 4,
              y1: (barY0 + barY1) / 2 + 4,
              alpha: base,
              pigment,
              radius: 4,
            });
        }
        const next = list[i + 1];
        const limit = Math.min(ix1, next ? camera.xOf(next.reach[0]) - 12 : ix1);
        const nx = Math.max(r0, ix0 + 2);
        const nw = L.measure(life.person.name, PERSON) * size;
        const shown = nx + nw < limit && nx < r1;
        const a = this.fades.get(`life:${life.person.id}`, shown, k);
        if (a <= 0) return;
        L.draw({
          text: life.person.name,
          style: PERSON,
          x: nx + nw / 2,
          y: y + rowH - 16,
          size,
          alpha: a * o * smooth(0, 1, a),
        });
        // With room to spare, who they were; and, where the record gives only one year, which one it is.
        const p = life.person;
        const known =
          life.known === "floruit"
            ? `active ${p.approx ? "c. " : ""}${fmtYear(life.from)}`
            : life.known === "born"
              ? `born ${p.approx ? "c. " : ""}${fmtYear(life.from)}`
              : life.known === "died"
                ? `died ${p.approx ? "c. " : ""}${fmtYear(life.to)}`
                : "";
        const about = known ? `${known} · ${p.role}` : p.role;
        const fits = (t: string) => nx + nw + 10 + L.measure(t, ROLE) * 12.5 < limit;
        const text = fits(about) ? about : known && fits(known) ? known : "";
        const ra = this.fades.get(`role:${p.id}`, shown && text !== "", k);
        if (ra > 0 && text) {
          const rw = L.measure(text, ROLE) * 12.5;
          L.draw({ text, style: ROLE, x: nx + nw + 10 + rw / 2, y: y + rowH - 16, size: 12.5, alpha: ra * o, tone: 1 });
        }
        this.hits.push({ x0: nx - 4, y0: y + 2, x1: nx + nw + 4, y1: y + rowH, life });
      });
    }
  }
}
